import { anonymousIdentityService } from '../services/anonymousIdentityService';

/**
 * Utility functions for working with anonymous identifiers
 */

// Cache for anonymous ID resolutions (admin-only)
const identityCache = new Map<string, { email: string; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Validates if a string is a valid anonymous ID format
 */
export function isValidAnonymousId(id: string): boolean {
  if (!id || typeof id !== 'string') {
    return false;
  }
  
  // Check format: anon_[12 alphanumeric characters]
  const pattern = /^anon_[a-z0-9]{12}$/;
  return pattern.test(id);
}

/**
 * Generates a new anonymous ID (wrapper for service)
 */
export function generateAnonymousId(): string {
  return anonymousIdentityService.generateAnonymousId();
}

/**
 * Resolves anonymous ID to email (admin-only with caching)
 */
export async function resolveAnonymousIdToEmail(
  anonymousId: string, 
  adminId: string, 
  reason: string,
  useCache: boolean = true
): Promise<string | null> {
  if (!isValidAnonymousId(anonymousId)) {
    return null;
  }

  // Check cache first if enabled
  if (useCache && identityCache.has(anonymousId)) {
    const cached = identityCache.get(anonymousId)!;
    const now = Date.now();
    
    if (now - cached.timestamp < CACHE_DURATION) {
      return cached.email;
    } else {
      identityCache.delete(anonymousId);
    }
  }

  try {
    const email = await anonymousIdentityService.resolveAnonymousId(anonymousId, adminId, reason);
    
    // Cache the result if enabled
    if (useCache && email) {
      identityCache.set(anonymousId, {
        email,
        timestamp: Date.now()
      });
    }
    
    return email;
  } catch (error) {
    console.error('Error resolving anonymous ID:', error);
    return null;
  }
}

/**
 * Batch resolves multiple anonymous IDs to emails (admin-only)
 */
export async function batchResolveAnonymousIds(
  anonymousIds: string[], 
  adminId: string, 
  reason: string
): Promise<Map<string, string>> {
  const results = new Map<string, string>();
  
  // Process in chunks to avoid overwhelming the database
  const chunks = chunkArray(anonymousIds, 10);
  
  for (const chunk of chunks) {
    const promises = chunk.map(async (id) => {
      const email = await resolveAnonymousIdToEmail(id, adminId, reason);
      return { id, email };
    });
    
    const chunkResults = await Promise.all(promises);
    
    chunkResults.forEach(({ id, email }) => {
      if (email) {
        results.set(id, email);
      }
    });
  }
  
  return results;
}

/**
 * Gets the current user's anonymous ID (from auth context)
 */
export async function getCurrentUserAnonymousId(uid: string): Promise<string | null> {
  try {
    const identity = await anonymousIdentityService.getIdentityByUid(uid);
    return identity?.id || null;
  } catch (error) {
    console.error('Error getting current user anonymous ID:', error);
    return null;
  }
}

/**
 * Migrates a username/userId to anonymous ID format
 */
export function migrateUserIdToAnonymous(oldUserId: string): string {
  // If it's already an anonymous ID, return as-is
  if (isValidAnonymousId(oldUserId)) {
    return oldUserId;
  }
  
  // For migration purposes, we'll need to look up the mapping
  // This should be handled by the migration service
  console.warn(`Legacy user ID detected: ${oldUserId}. Migration required.`);
  return oldUserId; // Return original for now
}

/**
 * Creates a display name for anonymous IDs (for UI purposes)
 */
export function createDisplayNameFromAnonymousId(anonymousId: string): string {
  if (!isValidAnonymousId(anonymousId)) {
    return 'Unknown User';
  }
  
  // Take the last 4 characters for display
  const suffix = anonymousId.slice(-4).toUpperCase();
  return `User ${suffix}`;
}

/**
 * Sanitizes anonymous ID for use in URLs
 */
export function sanitizeAnonymousIdForUrl(anonymousId: string): string {
  if (!isValidAnonymousId(anonymousId)) {
    throw new Error('Invalid anonymous ID format');
  }
  
  // Anonymous IDs are already URL-safe, but ensure proper encoding
  return encodeURIComponent(anonymousId);
}

/**
 * Extracts anonymous ID from URL parameters
 */
export function extractAnonymousIdFromUrl(urlParam: string): string | null {
  try {
    const decoded = decodeURIComponent(urlParam);
    return isValidAnonymousId(decoded) ? decoded : null;
  } catch (error) {
    console.error('Error extracting anonymous ID from URL:', error);
    return null;
  }
}

/**
 * Checks if user has permission to resolve anonymous IDs
 */
export function canResolveAnonymousIds(adminRole: string): boolean {
  const allowedRoles = ['super_admin', 'admin', 'moderator'];
  return allowedRoles.includes(adminRole);
}

/**
 * Creates anonymous conversation ID from participant IDs
 */
export function createAnonymousConversationId(participantIds: string[]): string {
  if (!participantIds.every(isValidAnonymousId)) {
    throw new Error('All participants must have valid anonymous IDs');
  }
  
  // Sort IDs to ensure consistent conversation ID regardless of order
  const sortedIds = [...participantIds].sort();
  
  // Create a hash-based conversation ID
  let hash = 0;
  const combined = sortedIds.join('_');
  
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return `conv_${Math.abs(hash).toString(36)}`;
}

/**
 * Validates conversation ID format
 */
export function isValidConversationId(conversationId: string): boolean {
  if (!conversationId || typeof conversationId !== 'string') {
    return false;
  }
  
  // Check format: conv_[alphanumeric]
  const pattern = /^conv_[a-z0-9]+$/;
  return pattern.test(conversationId);
}

/**
 * Clear identity resolution cache (admin utility)
 */
export function clearIdentityCache(): void {
  identityCache.clear();
}

/**
 * Get cache statistics (admin utility)
 */
export function getIdentityCacheStats(): {
  size: number;
  entries: Array<{ anonymousId: string; email: string; age: number }>;
} {
  const now = Date.now();
  const entries = Array.from(identityCache.entries()).map(([id, data]) => ({
    anonymousId: id,
    email: data.email,
    age: now - data.timestamp
  }));
  
  return {
    size: identityCache.size,
    entries
  };
}

/**
 * Helper function to chunk arrays
 */
function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  
  return chunks;
}

/**
 * Creates anonymous report ID
 */
export function createAnonymousReportId(reporterAnonymousId: string, reportedAnonymousId: string): string {
  if (!isValidAnonymousId(reporterAnonymousId) || !isValidAnonymousId(reportedAnonymousId)) {
    throw new Error('Invalid anonymous IDs provided');
  }
  
  const timestamp = Date.now();
  const combined = `${reporterAnonymousId}_${reportedAnonymousId}_${timestamp}`;
  
  let hash = 0;
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  return `report_${Math.abs(hash).toString(36)}`;
}

/**
 * Anonymous ID regular expressions for validation
 */
export const ANONYMOUS_ID_PATTERNS = {
  USER_ID: /^anon_[a-z0-9]{12}$/,
  CONVERSATION_ID: /^conv_[a-z0-9]+$/,
  REPORT_ID: /^report_[a-z0-9]+$/
} as const;

/**
 * Type guards for different ID types
 */
export const isAnonymousUserId = (id: string): boolean => ANONYMOUS_ID_PATTERNS.USER_ID.test(id);
export const isAnonymousConversationId = (id: string): boolean => ANONYMOUS_ID_PATTERNS.CONVERSATION_ID.test(id);
export const isAnonymousReportId = (id: string): boolean => ANONYMOUS_ID_PATTERNS.REPORT_ID.test(id);